import PropTypes from 'prop-types';
import { useRef, useState, useEffect } from 'react';
import { useI18n } from '../../../../context/I18nContext';
import { useModal } from '../../../../context/ModalContext';
import { useAvatar } from '../../../../context/AvatarContext';
import RoomAvatar from '../../../RoomAvatar';
import logger from '../../../../utils/logger';
import AvatarSelectionModal from './AvatarSelectionModal';
import './TopBar.css';

const charLimit = 50;

const TopBar = ({
  title = 'Chat Title',
  isChatDetailsHidden = false,
  onChatDetailsToggle = () => {},
  isEditMode = false,
  canRename = true,
  canSetRoomAvatar = true,
  canSave = true,
  defaultTitle = '',
  setTitle = () => {},
  onSaveClick = () => {},
  onCancelClick = () => {},
  isCreating = false,
  roomId,
  roomName: _roomName,
  isEncrypted = false,
  // Avatar state from useRoomEdit/useRoomCreate
  selectedAvatarOption = 'default',
  selectedAvatarFile = null,
  avatarUrls = [],
  defaultAvatarUrls = [], // Member avatars for modal's "Default" option
  hasAvatarChanged = false, // Whether avatar has been modified from original
  // Avatar selection functions
  selectDefaultAvatar = () => {},
  selectPresetAvatar = () => {},
  selectCustomAvatar = () => {},
}) => {
  const { t } = useI18n();
  const { showError } = useModal();
  const { setRoomAvatar, removeRoomAvatar } = useAvatar();
  const fileInputRef = useRef(null);
  const avatarSelectorRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Log avatar props for debugging
  useEffect(() => {
    logger.info('[TopBar] Avatar props received', {
      selectedAvatarOption,
      hasSelectedAvatarFile: !!selectedAvatarFile,
      avatarUrlsCount: avatarUrls?.length || 0,
      avatarUrls,
    });
  }, [selectedAvatarOption, selectedAvatarFile, avatarUrls]);
  
  // Generate preview URL based on selected avatar
  useEffect(() => {
    if (selectedAvatarOption === 'custom' && selectedAvatarFile) {
      // Create blob URL for custom file preview
      const blobUrl = URL.createObjectURL(selectedAvatarFile);
      logger.info('[TopBar] Setting previewUrl for custom file', { blobUrl });
      setPreviewUrl(blobUrl);
      
      // Cleanup blob URL on unmount or when file changes
      return () => {
        URL.revokeObjectURL(blobUrl);
      };
    } else if (selectedAvatarOption && selectedAvatarOption !== 'custom' && selectedAvatarOption !== 'default') {
      // It's a preset path - show the preset image
      logger.info('[TopBar] Setting previewUrl for preset', { presetPath: selectedAvatarOption });
      setPreviewUrl(selectedAvatarOption);
    } else {
      // For 'default' or null, don't override with previewUrl
      logger.info('[TopBar] Clearing previewUrl (default mode)');
      setPreviewUrl(null);
    }
  }, [selectedAvatarOption, selectedAvatarFile]);

  if (!isEditMode) {
    return (
      <div className="chatview-top-bar">
        <div className="chatview-top-bar-title">
          {title}
          {isEncrypted ? (
            <ion-icon className="chatview-top-bar-lock" name="lock-closed" title="Encrypted" />
          ) : (
            <ion-icon className="chatview-top-bar-lock unlocked" name="lock-open" title="Not Encrypted" />
          )}
        </div>
        <div className={`chatview-top-bar-button ${isChatDetailsHidden ? 'inactive' : ''}`} onClick={onChatDetailsToggle}>
          <ion-icon name="information-circle" />
        </div>
      </div>
    );
  }

  const handleAvatarClick = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSelectDefault = () => {
    selectDefaultAvatar();
    logger.info('Set avatar to default');
  };

  const handleSelectUpload = () => {
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleSelectPreset = (avatarPath) => {
    selectPresetAvatar(avatarPath);
    logger.info('Set avatar to preset:', avatarPath);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    selectCustomAvatar(file);
    logger.info('Set avatar to custom file:', file.name);

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const handleSave = async () => {
    // Handle avatar changes for existing rooms
    if (roomId) {
      try {
        if (selectedAvatarOption === 'default') {
          // Always remove server-side avatar when default is selected
          // Sends m.room.avatar state event to trigger conversation list refresh with latest member avatars
          await removeRoomAvatar(roomId);
          logger.info('Avatar removed successfully');
        } else if (hasAvatarChanged) {
          // For non-default selections, only update if changed
          if (selectedAvatarOption === 'custom') {
            // Upload custom file
            if (selectedAvatarFile) {
              await setRoomAvatar(roomId, selectedAvatarFile);
              logger.info('Custom avatar uploaded successfully');
            }
          } else {
            // It's a preset path - fetch and upload
            const response = await fetch(selectedAvatarOption);
            if (!response.ok) {
              throw new Error(`Failed to fetch preset avatar: ${response.statusText}`);
            }

            const blob = await response.blob();
            const filename = selectedAvatarOption.split('/').pop();
            const file = new File([blob], filename, { type: blob.type || 'image/png' });

            await setRoomAvatar(roomId, file);
            logger.info('Preset avatar uploaded successfully:', selectedAvatarOption);
          }
        }
      } catch (error) {
        logger.error('Failed to save avatar:', error);
        showError('Avatar Upload Failed', error.message || 'Could not upload room avatar');
        return; // Don't call onSaveClick if avatar save failed
      }
    }

    // Call the original save handler
    onSaveClick();
  };

  const handleCancel = () => {
    // Just call the cancel handler - the parent will reset state
    onCancelClick();
  };

  const renderAvatarSelector = () => {
    // For presets, avatarUrls already contains the preset path
    // For custom files, previewUrl contains the blob URL
    // For default, avatarUrls contains member avatars
    // RoomAvatar will prioritize previewUrl if it exists
    
    // For room edit mode, check if user has permission to change avatar
    const canChangeAvatar = isCreating || canSetRoomAvatar;
    
    return (
      <div 
        className={`editor-avatar-selector ${!canChangeAvatar ? 'disabled' : ''}`} 
        onClick={canChangeAvatar ? handleAvatarClick : undefined} 
        ref={avatarSelectorRef}
      >
        <RoomAvatar 
          avatarUrls={avatarUrls}
          previewUrl={previewUrl}
          placeholderText={title || 'Chat'}
        />
        {canChangeAvatar && (
          <img
            src="/images/ChangeProfileImageBadge.png"
            alt="Change Avatar"
            className="editor-change-avatar-badge"
          />
        )}
      </div>
    );
  };

  const renderTitleInput = () => (
    <div className="editor-title-input-container">
      <div className="editor-title-input-wrapper">
        <input
          id="editor-title-input"
          className="editor-title-input"
          type="text"
          value={title}
          placeholder={defaultTitle}
          maxLength={charLimit}
          disabled={!canRename}
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>
      <div className="editor-title-input-charcount">
        {t('ChatEdit.titleRemainingCharCount', { characterCount: charLimit - title.length })}
      </div>
    </div>
  );

  const renderButtons = () => (
    <div className="editor-buttons">
      <span className="editor-top-bar-button" onClick={handleCancel}>
        {t((isCreating) ? 'discard' : 'cancel')}
      </span>
      <span 
        className={`editor-top-bar-button ${!canSave ? 'disabled' : ''}`} 
        onClick={canSave ? handleSave : undefined}
        style={{ cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.5 }}
      >
        {t((isCreating) ? 'create' : 'save')}
      </span>
    </div>
  );

  return (
    <div className="editor-top-bar">
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept="image/*"
      />
      {renderAvatarSelector()}
      {renderTitleInput()}
      {renderButtons()}
      {isModalOpen && (
        <AvatarSelectionModal
          anchorElement={avatarSelectorRef.current}
          onClose={handleModalClose}
          onSelectDefault={handleSelectDefault}
          onSelectUpload={handleSelectUpload}
          onSelectPreset={handleSelectPreset}
          selectedOption={selectedAvatarOption}
          fallbackAvatarUrls={defaultAvatarUrls}
          placeholderText={title || 'Chat'}
        />
      )}
    </div>
  );
};

TopBar.propTypes = {
  title: PropTypes.string,
  isChatDetailsHidden: PropTypes.bool,
  onChatDetailsToggle: PropTypes.func,
  isEditMode: PropTypes.bool,
  canRename: PropTypes.bool,
  canSetRoomAvatar: PropTypes.bool,
  canSave: PropTypes.bool,
  defaultTitle: PropTypes.string,
  setTitle: PropTypes.func,
  onSaveClick: PropTypes.func,
  onCancelClick: PropTypes.func,
  isCreating: PropTypes.bool,
  roomId: PropTypes.string,
  roomName: PropTypes.string,
  isEncrypted: PropTypes.bool,
  // Avatar state from useRoomEdit/useRoomCreate
  selectedAvatarOption: PropTypes.string,
  selectedAvatarFile: PropTypes.object,
  avatarUrls: PropTypes.arrayOf(PropTypes.string),
  defaultAvatarUrls: PropTypes.arrayOf(PropTypes.string),
  hasAvatarChanged: PropTypes.bool,
  // Avatar selection functions
  selectDefaultAvatar: PropTypes.func,
  selectPresetAvatar: PropTypes.func,
  selectCustomAvatar: PropTypes.func,
};

export default TopBar;
